import React, { useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  PostCategory,
  PostListItemDto,
  useCreatePost,
  useDeletePost,
  usePost,
  usePosts,
  useUpdatePost,
} from '@shared/api/hooks/usePosts';
import { RichEditor } from '@shared/components/RichEditor';
import { MediaOrUrlField } from '@shared/components/MediaOrUrlField';

const CATEGORY_LABELS: Record<PostCategory, string> = {
  NEWS: 'News',
  MATCH_REPORT: 'Cronaca',
  INTERVIEW: 'Intervista',
  GALLERY: 'Galleria',
  VIDEO: 'Video',
};

const CATEGORY_COLORS: Record<PostCategory, string> = {
  NEWS: 'blue',
  MATCH_REPORT: 'green',
  INTERVIEW: 'purple',
  GALLERY: 'gold',
  VIDEO: 'red',
};

interface FormValues {
  titleIt: string;
  bodyIt: string;
  category: PostCategory;
  coverImage?: string;
  isPublished: boolean;
}

export function PostsPage(): React.ReactElement {
  const [filterCategory, setFilterCategory] = useState<PostCategory | undefined>(undefined);
  const [includeDrafts, setIncludeDrafts] = useState<boolean>(true);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form] = Form.useForm<FormValues>();

  const {
    data: pageData,
    isLoading,
    refetch,
    isFetching,
  } = usePosts({ category: filterCategory, includeDrafts, page, limit: 20 });
  const { data: editPost } = usePost(editId);
  const createMutation = useCreatePost();
  const updateMutation = useUpdatePost();
  const deleteMutation = useDeletePost();

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    form.setFieldsValue({ category: 'NEWS', isPublished: false });
    setModalOpen(true);
  };

  const openEdit = (p: PostListItemDto): void => {
    form.resetFields();
    setEditId(p.id);
    setModalOpen(true);
  };

  React.useEffect(() => {
    if (modalOpen && editId && editPost) {
      const t = (editPost.title ?? {}) as { it?: string };
      const b = (editPost.body ?? {}) as { it?: string };
      form.setFieldsValue({
        titleIt: t.it ?? '',
        bodyIt: b.it ?? '',
        category: editPost.category,
        coverImage: editPost.coverImage ?? undefined,
        isPublished: editPost.isPublished,
      });
    }
  }, [modalOpen, editId, editPost, form]);

  const onSubmit = async (): Promise<void> => {
    const v = await form.validateFields();
    const title = { it: v.titleIt };
    const body = { it: v.bodyIt };
    const input = {
      title,
      body,
      category: v.category,
      coverImage: v.coverImage || undefined,
      isPublished: v.isPublished,
    };
    try {
      if (editId) {
        await updateMutation.mutateAsync({ id: editId, input });
        message.success('Post aggiornato');
      } else {
        await createMutation.mutateAsync(input);
        message.success(v.isPublished ? 'Post pubblicato' : 'Bozza salvata');
      }
      setModalOpen(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore di salvataggio'));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Post eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore eliminazione');
    }
  };

  const onTogglePublish = async (p: PostListItemDto, next: boolean): Promise<void> => {
    try {
      await updateMutation.mutateAsync({ id: p.id, input: { isPublished: next } });
      message.success(next ? 'Pubblicato' : 'Riportato a bozza');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  const data = pageData?.data ?? [];
  const meta = pageData?.meta;

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            News & Post
          </Typography.Title>
          <Tag>{meta?.total ?? data.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Select
            placeholder="Categoria"
            style={{ width: 150 }}
            allowClear
            value={filterCategory}
            onChange={(v) => {
              setFilterCategory(v as PostCategory | undefined);
              setPage(1);
            }}
            options={(Object.keys(CATEGORY_LABELS) as PostCategory[]).map((c) => ({
              value: c,
              label: CATEGORY_LABELS[c],
            }))}
          />
          <Space>
            <Typography.Text type="secondary">Bozze</Typography.Text>
            <Switch
              checked={includeDrafts}
              onChange={(v) => {
                setIncludeDrafts(v);
                setPage(1);
              }}
            />
          </Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuovo post
          </Button>
        </Space>
      }
    >
      <Table<PostListItemDto>
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        pagination={
          meta
            ? {
                current: meta.page,
                pageSize: meta.limit,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }
            : false
        }
        columns={[
          {
            title: 'Cover',
            dataIndex: 'coverImage',
            width: 80,
            render: (v: string | null) =>
              v ? (
                <img
                  src={v}
                  alt=""
                  width={56}
                  height={40}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Titolo',
            render: (_v, r) => {
              const t = (r.title ?? {}) as { it?: string };
              return (
                <Typography.Text strong>{t.it ?? '(senza titolo)'}</Typography.Text>
              );
            },
          },
          {
            title: 'Categoria',
            dataIndex: 'category',
            width: 130,
            render: (v: PostCategory) => (
              <Tag color={CATEGORY_COLORS[v]}>{CATEGORY_LABELS[v]}</Tag>
            ),
          },
          {
            title: 'Stato',
            width: 110,
            render: (_v, r) =>
              r.publishedAt ? (
                <Tag color="green">Pubblicato</Tag>
              ) : (
                <Tag color="default">Bozza</Tag>
              ),
          },
          { title: 'Like', dataIndex: 'likesCount', width: 70, align: 'right' },
          {
            title: '',
            width: 200,
            align: 'right',
            render: (_v, r) => (
              <Space>
                <Switch
                  size="small"
                  checked={Boolean(r.publishedAt)}
                  onChange={(next) => onTogglePublish(r, next)}
                  loading={updateMutation.isPending}
                />
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm
                  title="Eliminare questo post?"
                  okText="Elimina"
                  cancelText="Annulla"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editId ? 'Modifica post' : 'Nuovo post'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={editId ? 'Salva' : 'Crea'}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnHidden
        width={720}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="titleIt"
            label="Titolo (IT)"
            rules={[{ required: true, max: 200 }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="category"
            label="Categoria"
            rules={[{ required: true }]}
          >
            <Select
              options={(Object.keys(CATEGORY_LABELS) as PostCategory[]).map((c) => ({
                value: c,
                label: CATEGORY_LABELS[c],
              }))}
            />
          </Form.Item>
          <Form.Item name="coverImage" label="Cover image">
            <MediaOrUrlField category="OTHER" placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="bodyIt"
            label="Corpo (IT)"
            rules={[{ required: true }]}
            valuePropName="value"
            trigger="onChange"
          >
            <RichEditor placeholder="Scrivi il post in italiano…" />
          </Form.Item>
          <Form.Item
            name="isPublished"
            label="Pubblicazione"
            valuePropName="checked"
            tooltip="Se attivo al primo salvataggio invia push NEW_POST a tutti i tifosi"
          >
            <Switch checkedChildren="Pubblicato" unCheckedChildren="Bozza" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
